/**
 * Timeline-specific filter vocabulary (lens / date / provenance). The
 * doc-kind taxonomy proper (icon / color / label / KIND_FILTERS) lives in
 * the shared `@components/DocumentKind` module so the legacy document
 * lists render with the same vocabulary.
 */

export type { KindColor, KindFilter } from '@components/DocumentKind';
export {
  KIND_COLOR,
  KIND_FILTERS,
  KIND_ICON,
  KIND_LABEL,
  kindIcon,
  kindLabel,
  kindStyle,
} from '@components/DocumentKind';

export interface DateFilter {
  id: string;
  label: string;
  /** ISO-8601 lower bound on `effective_date` (inclusive). null = unbounded. */
  since: string | null;
  /** ISO-8601 upper bound on `effective_date` (inclusive). null = unbounded. */
  before: string | null;
}

/**
 * Date filter buckets are computed relative to "now" so the labels stay
 * accurate as time passes (no hardcoded year strings). The `since` /
 * `before` ISO strings are passed straight through to the timeline
 * endpoint's bounds — the server filters, we don't.
 */
export function getDateFilters(now: Date = new Date()): DateFilter[] {
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const startOfLastYear = new Date(now.getFullYear() - 1, 0, 1);
  // `before` is exclusive on the next-bucket boundary so "last year" doesn't
  // bleed into Jan 1 of this year. The endpoint treats `before` as
  // inclusive on `effective_date`, so we use Dec 31 of the prior year.
  const endOfLastYear = new Date(now.getFullYear() - 1, 11, 31);

  return [
    { id: 'all', label: 'All time', since: null, before: null },
    {
      id: 'this-month',
      label: 'This month',
      since: startOfMonth.toISOString(),
      before: null,
    },
    {
      id: 'last-3mo',
      label: 'Last 3 months',
      since: threeMonthsAgo.toISOString(),
      before: null,
    },
    {
      id: 'this-year',
      label: `${now.getFullYear()}`,
      since: startOfYear.toISOString(),
      before: null,
    },
    {
      id: 'last-year',
      label: `${now.getFullYear() - 1}`,
      since: startOfLastYear.toISOString(),
      before: endOfLastYear.toISOString(),
    },
    {
      id: 'earlier',
      label: 'Earlier',
      since: null,
      before: startOfLastYear.toISOString(),
    },
  ];
}

// ── Lens filters (timeline envelope groups) ──────────────────────────

export interface LensFilter {
  id: 'all' | 'clinical' | 'operational' | 'admin';
  label: string;
}

export const LENS_FILTERS: LensFilter[] = [
  { id: 'all', label: 'All' },
  { id: 'clinical', label: 'Clinical' },
  { id: 'operational', label: 'Operational' },
  { id: 'admin', label: 'Admin' },
];

// ── Provenance glyph ──────────────────────────────────────────────────

/** Returns ✦ for AI-derived sources, ✎ for human-entered (system_generated). */
export function provenanceGlyph(sourceChannel: string | null | undefined): '✦' | '✎' {
  return sourceChannel === 'system_generated' ? '✎' : '✦';
}
